import type {
  ApprovalDeployPreview,
  ApprovalEmailPreview,
  ApprovalFact,
  ApprovalInvoicePreview,
  ApprovalMemoryPreview,
  ApprovalOfferTermsPreview,
  ApprovalPreview,
  ApprovalTaskProposalsPreview,
} from '@fos/shared/contracts/approvals';
import { TASK_PROPOSAL_STATUS_LABELS } from '@fos/shared/labels';
import { ArrowRight, Lock, Paperclip, TriangleAlert } from 'lucide-react';
import { useId, useState } from 'react';
import { Link } from 'react-router';
import { cn } from '../../../lib/cn';
import { formatDate, formatDuration, formatMoney } from '../../../lib/format';
import { Mono } from '../../../ui/Mono';
import { Dot, type Tone } from '../../../ui/Status';
import { DiffView } from '../../knowledge';
import { TOUCH_TEXT } from '../../mobile';

/**
 * Pełny podgląd akcji (MVP §10: „Krótkie podsumowanie musi prowadzić do pełnego podglądu”; DESIGN §5 „Akceptacja”):
 * blok na bg/surface. Mail — odbiorcy i cała treść („Pokaż całą wiadomość”), faktura — klient, pozycje, kwoty,
 * waluta, termin, zadania — lista propozycji i link do przeglądu spotkania, pamięć — diff, oferta — wersja i odbiorca,
 * deploy — repo, commit, środowisko, sprawdzenia. Kwoty pokazuje serwer tylko z „Widzi stawki i kwoty”.
 */
export function ApprovalPreviewView({
  preview,
  showFacts = true,
  showDescription = true,
  className,
}: {
  preview: ApprovalPreview;
  /** fakty (klucz–wartość) pod podglądem w jednej linii; ekran szczegółów pokazuje je wyżej jako wiersze */
  showFacts?: boolean;
  /** opis wdrożenia w podglądzie (ekran szczegółów pokazuje go pod nagłówkiem, Figma 03 mobile) */
  showDescription?: boolean;
  className?: string;
}) {
  const facts = showFacts && preview.kind !== 'deploy' ? preview.facts : [];
  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <PreviewBody preview={preview} showDescription={showDescription} />
      {(facts.length > 0 || preview.links.length > 0) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-caption">
          {facts.map((f) => (
            <span key={f.label} className="text-fg-muted">
              {f.label} <span className="text-fg-secondary">{f.value}</span>
            </span>
          ))}
          {preview.links.map((l) => (
            <Link
              key={l.path}
              to={l.path}
              className="inline-flex items-center gap-1 rounded-xs text-caption font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
            >
              {l.label}
              <ArrowRight size={14} strokeWidth={1.75} aria-hidden />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function PreviewBody({ preview, showDescription }: { preview: ApprovalPreview; showDescription: boolean }) {
  switch (preview.kind) {
    case 'email':
      return <EmailPreview preview={preview} />;
    case 'invoice':
      return <InvoicePreview preview={preview} />;
    case 'task_proposals':
      return <TaskProposalsPreview preview={preview} />;
    case 'memory':
      return <MemoryPreview preview={preview} />;
    case 'offer_terms':
      return <OfferTermsPreview preview={preview} />;
    case 'deploy':
      return <DeployPreview preview={preview} showDescription={showDescription} />;
    case 'generic':
      return null;
  }
}

function Surface({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('rounded-lg bg-surface p-4', className)}>{children}</div>;
}

export function Facts({ facts }: { facts: ApprovalFact[] }) {
  return (
    <dl className="divide-y divide-line border-y border-line">
      {facts.map((f) => (
        <div key={f.label} className="flex items-baseline justify-between gap-4 py-2.5 text-body-s">
          <dt className="shrink-0 text-fg-muted">{f.label}</dt>
          <dd className="min-w-0 text-right text-fg">{f.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function HiddenNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-start gap-2 text-body-s text-fg-muted">
      <Lock size={14} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

const BODY_PREVIEW_CHARS = 240;

/** Początek treści do pełnego słowa (karta: „Pokaż całą wiadomość” rozwija resztę). */
function clip(body: string): string {
  const head = body.slice(0, BODY_PREVIEW_CHARS);
  const cut = head.lastIndexOf(' ');
  return `${(cut > BODY_PREVIEW_CHARS * 0.6 ? head.slice(0, cut) : head).trimEnd()}…`;
}

function EmailPreview({ preview }: { preview: ApprovalEmailPreview }) {
  const [full, setFull] = useState(false);
  const bodyId = useId();
  const body = preview.body;
  const long = body !== null && (body.length > BODY_PREVIEW_CHARS || body.split('\n').length > 4);
  const shown = body === null ? null : full || !long ? body : clip(body);
  return (
    <Surface>
      <p className="text-caption text-fg-secondary">
        <span className="text-fg-muted">Do </span>
        {preview.to.map((r, i) => (
          <span key={r.email}>
            {i > 0 && ', '}
            <span className="text-fg">{r.name ? `${r.name} <${r.email}>` : r.email}</span>
          </span>
        ))}
      </p>
      <p className="mt-1 text-caption text-fg-secondary">
        <span className="text-fg-muted">Temat </span>
        <span className="text-fg">{preview.subject}</span>
      </p>
      {shown === null ? (
        <div className="mt-3">
          <HiddenNote>Treść zawiera kwoty. Widzi ją osoba z uprawnieniem „Widzi stawki i kwoty”.</HiddenNote>
        </div>
      ) : (
        <p id={bodyId} className="mt-3 text-body-s whitespace-pre-wrap break-words text-fg">
          {shown}
        </p>
      )}
      {long && (
        <button
          type="button"
          aria-expanded={full}
          aria-controls={bodyId}
          onClick={() => setFull((v) => !v)}
          className={`mt-2 rounded-xs text-caption font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent ${TOUCH_TEXT}`}
        >
          {full ? 'Zwiń wiadomość' : 'Pokaż całą wiadomość'}
        </button>
      )}
      {preview.attachments.length > 0 && (
        <ul className="mt-3 flex flex-col gap-1">
          {preview.attachments.map((a) => (
            <li key={a.name} className="flex items-center gap-1.5 text-caption text-fg-secondary">
              <Paperclip size={14} strokeWidth={1.75} aria-hidden />
              {a.name}
            </li>
          ))}
        </ul>
      )}
    </Surface>
  );
}

function InvoicePreview({ preview }: { preview: ApprovalInvoicePreview }) {
  const amounts = preview.totals.net !== undefined;
  return (
    <Surface>
      <p className="text-body-s font-medium text-fg">
        {preview.client.name}
        {preview.client.nip && <span className="font-normal text-fg-muted"> · NIP {preview.client.nip}</span>}
      </p>
      <ul className="mt-3 divide-y divide-line">
        {preview.lines.map((l, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: pozycje faktury nie mają id (kolejność z payloadu)
          <li key={i} className="flex items-baseline justify-between gap-4 py-2 text-body-s">
            <span className="min-w-0 text-fg-secondary">{l.description}</span>
            <span className="shrink-0 text-right">
              {l.seconds !== null && <Mono className="text-fg-muted">{formatDuration(l.seconds)}</Mono>}
              {l.amount !== undefined && (
                <Mono className="ml-3 text-fg">{formatMoney(l.amount, preview.currency)}</Mono>
              )}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-2 flex items-baseline justify-between border-t border-line pt-2 text-body-s">
        <span className="font-medium text-fg">Razem netto</span>
        <span>
          <Mono className="text-fg-muted">{formatDuration(preview.totals.seconds)}</Mono>
          {amounts && (
            <Mono className="ml-3 font-semibold text-fg">
              {formatMoney(preview.totals.net as string, preview.currency)}
            </Mono>
          )}
        </span>
      </div>
      {!amounts && (
        <div className="mt-3">
          <HiddenNote>Kwoty widzi osoba z uprawnieniem „Widzi stawki i kwoty”.</HiddenNote>
        </div>
      )}
    </Surface>
  );
}

const PROPOSAL_TONES: Record<string, Tone> = { proposed: 'accent', accepted: 'success', rejected: 'neutral' };

function TaskProposalsPreview({ preview }: { preview: ApprovalTaskProposalsPreview }) {
  return (
    <Surface>
      {preview.meeting && (
        <p className="text-caption text-fg-muted">
          Spotkanie: <span className="text-fg">{preview.meeting.title}</span> · {formatDate(preview.meeting.startsAt)}
        </p>
      )}
      <ul className="mt-2 flex flex-col gap-2">
        {preview.proposals.map((p) => (
          <li key={p.id} className="flex items-start gap-2.5 text-body-s">
            <Dot tone={PROPOSAL_TONES[p.status] ?? 'neutral'} className="mt-[7px]" />
            <span className="min-w-0 flex-1">
              <span className={cn('text-fg', p.status === 'rejected' && 'text-fg-muted line-through')}>{p.title}</span>
              {(p.assigneeName || p.dueDate) && (
                <span className="block text-caption text-fg-muted">
                  {[p.assigneeName, p.dueDate ? `termin ${formatDate(p.dueDate)}` : null].filter(Boolean).join(' · ')}
                </span>
              )}
            </span>
            {p.status !== 'proposed' && (
              <span className="shrink-0 text-caption text-fg-muted">
                {TASK_PROPOSAL_STATUS_LABELS[p.status as keyof typeof TASK_PROPOSAL_STATUS_LABELS] ?? p.status}
              </span>
            )}
          </li>
        ))}
      </ul>
    </Surface>
  );
}

function MemoryPreview({ preview }: { preview: ApprovalMemoryPreview }) {
  if (!preview.diff) {
    return (
      <Surface>
        <HiddenNote>Profile kontekstu widzą właściciel, administratorzy i managerowie.</HiddenNote>
      </Surface>
    );
  }
  return <DiffView lines={preview.diff} aria-label={`Zmiany: ${preview.profileLabel}`} />;
}

function OfferTermsPreview({ preview }: { preview: ApprovalOfferTermsPreview }) {
  return (
    <Surface>
      <ul className="flex flex-col gap-2">
        {preview.changes.map((c) => (
          <li key={c.field} className="text-body-s">
            <span className="text-fg-muted">{c.label}: </span>
            {c.before === null && c.after === null ? (
              <span className="text-fg-muted">zmiana (kwoty widzi osoba z „Widzi stawki i kwoty”)</span>
            ) : (
              <>
                <span className="text-fg-secondary line-through">{c.before ?? '—'}</span>
                <span className="text-fg-muted"> → </span>
                <span className="whitespace-pre-wrap text-fg">{c.after ?? '—'}</span>
              </>
            )}
          </li>
        ))}
      </ul>
    </Surface>
  );
}

const CHECK_TONES: Record<ApprovalDeployPreview['checks'][number]['status'], Tone> = {
  passed: 'success',
  failed: 'danger',
  skipped: 'neutral',
  pending: 'warning',
};

/**
 * Wdrożenie (MVP §10, Figma 03 „Pełne szczegóły”): repozytorium, konkretny commit (mono), branch, środowisko, wynik
 * testów („12/12”), powrót do poprzedniej wersji i nieodwracalność. `showDescription = false`, gdy opis stoi już
 * w nagłówku ekranu szczegółów.
 */
function DeployPreview({ preview, showDescription }: { preview: ApprovalDeployPreview; showDescription: boolean }) {
  const description = showDescription ? preview.description : null;
  const tests = preview.checksSummary?.replace(/^testy\s+/i, '') ?? null;
  return (
    <Surface>
      {description && <p className="text-body-s text-fg">{description}</p>}
      <dl
        aria-label="Szczegóły wdrożenia"
        className={cn('grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-body-s', description && 'mt-3')}
      >
        <dt className="text-fg-muted">Repozytorium</dt>
        <dd className="min-w-0 truncate text-fg" title={preview.repo}>
          {preview.repo}
        </dd>
        <dt className="text-fg-muted">Commit</dt>
        <dd className="min-w-0 text-fg">
          <Mono>{preview.commit.slice(0, 12)}</Mono>
          {preview.branch && <span className="break-all text-fg-muted"> · {preview.branch}</span>}
        </dd>
        <dt className="text-fg-muted">Środowisko</dt>
        <dd className="text-fg">{preview.environment}</dd>
        {tests && (
          <>
            <dt className="text-fg-muted">Testy</dt>
            <dd className="text-fg">
              <Mono>{tests}</Mono>
            </dd>
          </>
        )}
        {preview.rollback && (
          <>
            <dt className="text-fg-muted">Powrót</dt>
            <dd className="text-fg">{preview.rollback}</dd>
          </>
        )}
      </dl>
      {preview.checks.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1" aria-label="Sprawdzenia">
          {preview.checks.map((c) => (
            <li key={c.name} className="inline-flex items-center gap-1.5 text-caption text-fg-secondary">
              <Dot tone={CHECK_TONES[c.status]} />
              <Mono>{c.name}</Mono>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-3 flex items-start gap-2 text-caption text-fg-muted">
        <TriangleAlert size={14} strokeWidth={1.75} aria-hidden className="mt-px shrink-0" />
        <span>
          Wdrożenia nie da się cofnąć.
          {preview.rollback ? ' W razie problemu przywrócisz poprzednią wersję.' : ''}
        </span>
      </p>
    </Surface>
  );
}
